import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculateDeckAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];
  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { deck, craftsmanQuality: quality, propertyType } = subjectImprovements;
  const { deck: compDeck, craftsmanQuality: compDwellingQuality } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  if (
    isUndefined(zipCode) ||
    isUndefined(compZipCode) ||
    isUndefined(propertyType) ||
    isUndefined(quality) ||
    isUndefined(compDwellingQuality) ||
    quality < 1
  ) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (deck?.type === undefined && compDeck?.type === undefined) {
    return undefined;
  }

  return adjCalculator.getDeckAdj({
    subject: {
      type: deck?.type,
      hasFeature: ORDER_FEATURES.deck.hasFeature(subjectImprovements),
      sqft: deck?.area ? AloftArea.schema.parse(deck?.area) : undefined,
      quality: deck?.quality ?? quality,
      propertyType,
      zipCode,
    },
    comp: {
      type: compDeck?.type,
      hasFeature: ORDER_FEATURES.deck.hasFeature(compImprovements),
      sqft: compDeck?.area ? AloftArea.schema.parse(compDeck?.area) : undefined,
      quality: compDeck?.quality ?? compDwellingQuality,
      propertyType,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

export const selectDeckAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'deck'),
    (state, id: string) => id,
  ],
  calculateDeckAdj,
);

export const selectDeckAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'deck'),
    selectCompSequence,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateDeckAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);
